/**
 * @typedef { import('../../time.js').YearMonthPeriod } YearMonthPeriod
 * @typedef { import('../../time.js').YearMonthPeriodObject } YearMonthPeriodObject
 * @typedef { import('../../time.js').YearMonth } YearMonth
 */

export class Report {
  /**
   * @param {YearMonthPeriod} period
   * @param {?Cohort[]} cohorts
   */
  constructor(period, cohorts) {
    /** @type {YearMonthPeriod} */
    this.period = period;

    /** @type {?Cohort[]} */
    this.cohorts = cohorts;
  }

  /** @returns {YearMonthPeriod} */
  getPeriod() {
    return this.period;
  }

  /**
   * @param {YearMonth} month
   * @returns {Object}
   */
  getCohortByMonth(month) {
    const cohort = this._findCohortByMonth(month);
    if(cohort === null) {
      return new NullCohort(month);
    }

    return cohort;
  }

  /**
   * @param {YearMonth} month
   * @returns {?Object}
   */
  _findCohortByMonth(month) {
    if(this.cohorts === null) {
      return null;
    }

    for(const cohort of this.cohorts) {
      if(cohort.getYearMonth().isEqual(month)) {
        return cohort;
      }
    }

    return null;
  }
}

//-----------------------------------------------------------------------------

export class Cohort {
  /**
   * @param {YearMonth} yearMonth
   * @param {number} size 
   * @param {?MonthMetric[]} metrics
   */
  constructor(yearMonth, size, metrics) {
    /** @type {YearMonth} */
    this.yearMonth = yearMonth;

    /** @type {number} */
    this.size = size;

    /** @type {?MonthMetric[]} */
    this.metrics = metrics;
  }

  getName() {
    return getCohortName(this.yearMonth);
  }

  getYearMonth() {
    return this.yearMonth;
  }

  getSize() {
    return this.size;
  }

  /**
   * @param {YearMonth} month 
   * @returns {number}
   */
  getMetricValueByMonth(month) {
    const metric = this._findMetricByMonth(month);
    if(metric === null) {
      return 0;
    }

    return metric.getValue();
  }

  /**
   * @param {YearMonth} month 
   * @returns {?MonthMetric}
   */
  _findMetricByMonth(month) {
    const metrics = this.metrics;
    if(metrics === null) {
      return null;
    }

    for(const metric of metrics) {
      if(metric.getMonth().isEqual(month)) {
        return metric;
      }
    }

    return null;
  }
}

class NullCohort {
  /** @param {YearMonth} yearMonth */
  constructor(yearMonth) {
    /** @type {YearMonth} */
    this.yearMonth = yearMonth;
  }

  getName() {
    return getCohortName(this.yearMonth);
  }

  getYearMonth() {
    return this.yearMonth;
  }

  getSize() {
    return 0;
  }

  getMetricValueByMonth() {
    return 0;
  }
}

/**
 * @param {YearMonth} yearMonth 
 * @returns {string}
 */
function getCohortName(yearMonth) {
  return yearMonth.toString();
}

//-----------------------------------------------------------------------------

export class MonthMetric {
  /**
   * @param {YearMonth} yearMonth 
   * @param {number} value
   */
  constructor(yearMonth, value) {
    /** @type {YearMonth} */
    this.yearMonth = yearMonth;

    /** @type {number} */
    this.value = value;
  }

  getMonth() {
    return this.yearMonth;
  }

  getValue() {
    return this.value;
  }
}
